import { Component } from '@theme/component';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Quantity-tier upsell for the product page ("1 / 2 / 3 pieces"), the pieces split across colorways
 * with a − / + row per colour, plus an optional charm step. Adds every chosen variant and charm in
 * one request and hands the result to the cart drawer the same way Horizon's product form does.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement[]} tiers
 * @property {HTMLElement[]} rows
 * @property {HTMLElement[]} counts
 * @property {HTMLElement} total
 * @property {HTMLButtonElement} submit
 * @property {HTMLElement} hint
 * @property {HTMLElement} [shipFill]
 * @property {HTMLElement} [progressFill]
 * @property {HTMLElement[]} [steps]
 * @property {HTMLElement} [error]
 * @property {HTMLButtonElement} [charmYes]
 * @property {HTMLButtonElement} [charmNo]
 * @property {HTMLElement} [charmPanel]
 * @property {HTMLButtonElement[]} [charms]
 * @property {HTMLButtonElement} [charmMore]
 * @property {HTMLImageElement} [previewImg]
 * @property {HTMLElement} [previewName]
 * @property {HTMLElement} [pack]
 * @property {HTMLInputElement[]} [packs]
 * @property {HTMLElement} [packPick]
 * @property {HTMLButtonElement[]} [slots]
 * @property {HTMLButtonElement[]} [mates]
 * @property {HTMLDialogElement} [packSheet]
 * @property {HTMLElement} [packCount]
 * @property {HTMLElement} [packEmpty]
 * @property {HTMLElement} [submitLabel]
 * @property {HTMLElement} [bandOffer]
 * @property {HTMLInputElement[]} [bandOpts]
 * @property {HTMLButtonElement[]} [seconds]
 * @property {HTMLElement} [bandPick]
 * @property {HTMLElement} [tier3Price]
 * @property {HTMLElement} [tier3Was]
 * @property {HTMLDialogElement} [bandSheet]
 * @property {HTMLElement} [charmPicked]
 * @property {HTMLElement} [charmPickedText]
 * @property {HTMLElement} [secondPicked]
 *
 * @extends Component<Refs>
 */
class ClaspLabBundle extends Component {
  #busy = false;
  #charmsOn = false;
  #charmsExpanded = false;
  /** Row indexes in the order they were last increased; trimming removes the oldest first. */
  #history = [];
  /** Claspie packs (snippets/cl-pack.liquid): variant ids the shopper picked, in order. */
  #picks = [];
  /** Add to cart is waiting for the shopper's Claspie picks: tapping it opens the list instead */
  #waiting = false;
  #packVibe = 'all';
  #packMetal = 'all';

  connectedCallback() {
    super.connectedCallback();
    const rows = this.refs.rows || [];
    rows.forEach((row, i) => {
      for (let n = 0; n < this.#count(i); n++) this.#history.push(i);
    });
    this.#applyTier(this.#tierIndex());
    if (this.#packOn) this.selectPack();
    if (this.#bandOn) this.selectBandTier();
    this.#setupSticky();
    this.#charmFromUrl();
    // Colour preview (quick-add window on phones): tapping a row's photo or name shows that colour big
    this.addEventListener('click', this.#onRowClick);
    const first = rows.findIndex((r) => r.dataset.default === 'true');
    if (first >= 0) rows[first]?.classList.add('is-previewed');
  }

  /** @param {Event} event */
  #onRowClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const row = target?.closest('.cl-stepper');
    if (!row || target?.closest('.cl-stepper__btn')) return;
    const i = (this.refs.rows || []).indexOf(/** @type {HTMLElement} */ (row));
    if (i >= 0) this.#preview(i);
  };

  /** @param {number} i */
  #preview(i) {
    const { previewImg, previewName, rows = [] } = this.refs;
    const row = rows[i];
    if (!row) return;
    rows.forEach((r) => r.classList.toggle('is-previewed', r === row));
    // Product page: jump the main gallery to this colour's photo (matched by image file name)
    if (row.dataset.image && !this.closest('dialog')) {
      const file = (/** @type {string} */ url) => url.split('?')[0].split('/').pop() || '';
      const want = file(row.dataset.image);
      const section = this.closest('.shopify-section') || document;
      const slideshow = /** @type {any} */ (section.querySelector('media-gallery slideshow-component'));
      const slides = /** @type {HTMLElement[]} */ (slideshow?.refs?.slides || []);
      const index = slides.findIndex((slide) => {
        const img = slide.querySelector('img');
        return img && file(img.currentSrc || img.src) === want;
      });
      if (index >= 0 && typeof slideshow.select === 'function') slideshow.select(index);
      // …and the title "Sport · Night" follows the colour you're looking at
      const title = section.querySelector('h1.cl-product-name');
      const colour = row.dataset.colour;
      if (title && colour && title.childElementCount === 0 && title.textContent?.includes(' · ')) {
        const base = title.textContent.split(' · ')[0]?.trim();
        title.textContent = `${base} · ${colour}`;
      }
    }
    if (!previewImg || !row.dataset.image) return;
    if (previewName) previewName.textContent = row.dataset.colour || '';
    const src = row.dataset.image;
    if (previewImg.getAttribute('src') === src) return;
    previewImg.classList.add('is-swapping');
    const next = new Image();
    next.onload = next.onerror = () => {
      previewImg.removeAttribute('srcset');
      previewImg.src = src;
      previewImg.classList.remove('is-swapping');
    };
    next.src = src;
  }

  /**
   * ?charm=<handle> (quiz result → product page): open the charm step with that charm picked, so the
   * shopper keeps the look they were matched with. Charms outside the grid's first 24 are fetched.
   */
  async #charmFromUrl() {
    const handle = new URLSearchParams(window.location.search).get('charm');
    if (!handle || !this.refs.charmYes || this.closest('dialog')) return;
    let button = /** @type {HTMLButtonElement | null} */ (
      this.querySelector(`.cl-upsell-charm[data-handle="${CSS.escape(handle)}"]`)
    );
    if (!button) button = await this.#addCharmButton(handle);
    if (!button || button.disabled) return;
    const item = button.closest('.cl-upsell__item');
    if (item instanceof HTMLElement) {
      item.hidden = false;
      item.removeAttribute('data-extra');
      // Shown first with CSS order: moving the node would shift the index-based toggleCharm handlers
      item.style.order = '-1';
    }
    this.#setCharmMode(true);
    button.setAttribute('aria-pressed', 'true');
    this.#render();
  }

  /**
   * Copies the first charm tile for a charm that isn't in the grid.
   * @param {string} handle
   * @returns {Promise<HTMLButtonElement | null>}
   */
  async #addCharmButton(handle) {
    const template = this.querySelector('.cl-upsell__item');
    if (!(template instanceof HTMLElement)) return null;
    try {
      const product = await fetch(`/products/${encodeURIComponent(handle)}.js`).then((r) => (r.ok ? r.json() : null));
      const variant = product?.variants?.find((/** @type {any} */ v) => v.available);
      if (!product || product.type !== 'Charm' || !variant) return null;
      // Only charm types confirmed on this band (snippets/cl-compat.liquid → data-charm-kinds)
      const tags = product.tags || [];
      const kind = tags.includes('clip') ? 'clip' : tags.includes('bar') ? 'bar' : 'ring';
      let kinds = [];
      try {
        kinds = JSON.parse(this.dataset.charmKinds || '[]');
      } catch (e) {}
      if (!kinds.includes(kind)) return null;
      const item = /** @type {HTMLElement} */ (template.cloneNode(true));
      const button = item.querySelector('.cl-upsell-charm');
      if (!(button instanceof HTMLButtonElement)) return null;
      // @ts-ignore set by snippets/cl-preorder.liquid when pre-orders are on
      const preorder = window.ClaspLabPreorder;
      const isPre = Boolean(preorder && (product.tags || []).includes('preorder'));
      item.dataset.tags = (product.tags || []).join(' ').toLowerCase();
      button.dataset.variantId = String(variant.id);
      button.dataset.price = String(variant.price);
      button.dataset.preorder = String(isPre);
      button.dataset.handle = handle;
      button.setAttribute('aria-pressed', 'false');
      button.setAttribute('on:click', `/toggleCharm/${(this.refs.charms || []).length}`);
      const img = button.querySelector('img');
      if (img) {
        img.removeAttribute('srcset');
        img.src = `${product.featured_image}${product.featured_image?.includes('?') ? '&' : '?'}width=300`;
      }
      const name = button.querySelector('.cl-upsell-charm__name');
      if (name) name.textContent = product.title;
      const price = button.querySelector('.cl-upsell-charm__price');
      if (price) price.textContent = `+${this.#money(variant.price)}`;
      button.querySelector('.cl-preorder-mini')?.remove();
      template.parentElement?.append(item);
      return button;
    } catch (e) {
      return null;
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#onRowClick);
    this.#stickyObserver?.disconnect();
    this.#totalObserver?.disconnect();
    if (this.#onScroll) window.removeEventListener('scroll', this.#onScroll);
  }

  /** @type {IntersectionObserver | null} */
  #stickyObserver = null;
  /** @type {MutationObserver | null} */
  #totalObserver = null;
  /** @type {(() => void) | null} */
  #onScroll = null;

  /** Show the sticky bar (phones and laptops) once the main Add to cart has scrolled above the screen (sets: or is still below it). */
  #setupSticky() {
    const sticky = /** @type {HTMLElement | null} */ (this.querySelector('[ref="sticky"]'));
    const stickyTotals = [...this.querySelectorAll('[ref="stickyTotal"]')];
    const { submit, total } = this.refs;
    if (!sticky || !submit) return;
    // Inside the quick-add modal the main button is always in view: no sticky bar.
    if (this.closest('dialog')) {
      sticky.remove();
      return;
    }
    // Lift it out of any transformed/contained ancestor so position: fixed is relative to the screen
    document.body.append(sticky);
    sticky.querySelector('button')?.addEventListener('click', () => {
      // Waiting for picks: submit.click() opens the Claspie list (see add)
      submit.click();
    });
    // Hidden again while another add button (the Complete-the-look set) is on screen, so the bar never covers it; the
    // same for the product name and price (Cris, 3 Nov, design B: on set pages the bar sat on the name on load)
    const section = this.closest('.shopify-section') || document;
    const own = [...section.querySelectorAll('h1.cl-product-name, .product-details product-price, .cl-setin__price')];
    const others = () => [...document.querySelectorAll('.cl-complete .button, .cl-complete button, .cl-flook__add, .cl-pairs__add'), ...own];
    // Sets (data-sticky-early, Cris 23 Oct): also while the main button isn't fully on screen yet (short phones)
    const early = this.hasAttribute('data-sticky-early');
    const update = () => {
      const r = submit.getBoundingClientRect();
      const past = r.bottom < 0 || (early && r.bottom > window.innerHeight);
      const zone = window.innerHeight - 120; // the strip at the bottom where the bar sits
      const covering = others().some((el) => {
        const r = el.getBoundingClientRect();
        return r.height > 0 && r.bottom > zone && r.top < window.innerHeight;
      });
      sticky.dataset.visible = String(past && !covering);
    };
    this.#stickyObserver = new IntersectionObserver(update, { threshold: 0 });
    this.#stickyObserver.observe(submit);
    // Plain scroll check as well, so the bar also works where observers are throttled
    this.#onScroll = update;
    window.addEventListener('scroll', this.#onScroll, { passive: true });
    if (total && stickyTotals.length) {
      // The price shows twice: under the name (phones) and inside the button (laptops); the bar's button says the same
      // as the main one ("Choose your Claspie", "Pick 2 more", "Add to cart"; design B, Cris 3 Nov)
      const stickyLabel = sticky.querySelector('.cl-sticky-atc__btn > span:first-child');
      const { submitLabel } = this.refs;
      const sync = () => {
        stickyTotals.forEach((el) => (el.textContent = total.textContent));
        if (stickyLabel && submitLabel) stickyLabel.textContent = submitLabel.textContent;
      };
      this.#totalObserver = new MutationObserver(sync);
      this.#totalObserver.observe(total, { childList: true, characterData: true, subtree: true });
      if (submitLabel) this.#totalObserver.observe(submitLabel, { childList: true, characterData: true, subtree: true });
      sync();
    }
  }

  get #moneyFormat() {
    return this.dataset.moneyFormat || '€{{amount_with_comma_separator}}';
  }

  /** Free-shipping threshold in the visitor's currency, cents (0 = no hint) */
  get #threshold() {
    const local = Number(this.dataset.freeShippingCents || 0);
    if (local) return local;
    // Not in euros (Cris, 3 Nov): convert the euro threshold like the checkout does, or 0 (hint hidden) when we can't
    // @ts-ignore set by assets/clasplab-cart-extras.js
    return window.ClaspLabFx?.toLocal(Number(this.dataset.freeShippingEur || 0)) || 0;
  }

  /** @returns {number} */
  #tierIndex() {
    const { tiers = [] } = this.refs;
    const i = tiers.findIndex((t) => t.checked);
    return i === -1 ? 0 : i;
  }

  /** No tier cards: the colour counts are the quantity (Cris, after the 5 Oct audit). */
  get #hasTiers() {
    return (this.refs.tiers || []).length > 0;
  }

  /** @returns {{ qty: number, free: number, label: string }} */
  #tier(index = this.#tierIndex()) {
    if (!this.#hasTiers) return { qty: Math.max(1, this.#picked()), free: 0, label: '' };
    const input = this.refs.tiers?.[index];
    return {
      qty: Number(input?.dataset.qty || 1),
      free: Number(input?.dataset.free || 0),
      label: input?.dataset.label || '',
    };
  }

  /** @param {number} i */
  #count(i) {
    return Number(this.refs.rows?.[i]?.dataset.count || 0);
  }

  /** @param {number} i @param {number} value */
  #setCount(i, value) {
    const row = this.refs.rows?.[i];
    if (!row) return;
    row.dataset.count = String(value);
    const label = this.refs.counts?.[i];
    if (label) {
      label.textContent = String(value);
      label.toggleAttribute('data-zero', value === 0);
    }
  }

  #picked() {
    return (this.refs.rows || []).reduce((sum, _row, i) => sum + this.#count(i), 0);
  }

  /** @param {number} i */
  #available(i) {
    return this.refs.rows?.[i]?.dataset.available !== 'false';
  }

  /** Declarative: on:change="/selectTier" on each radio. */
  selectTier() {
    this.#applyTier(this.#tierIndex());
  }

  /**
   * Fit the colour counts to the tier: trim the most recent additions when there are too many,
   * top up the default colour (or the first available one) when there are too few.
   * @param {number} index
   */
  #applyTier(index) {
    const { tiers = [], rows = [] } = this.refs;
    tiers.forEach((t, i) => t.closest('.cl-tier')?.classList.toggle('cl-tier--selected', i === index));
    if (!this.#hasTiers) {
      rows.forEach((_r, i) => this.#setCount(i, this.#count(i)));
      this.#render();
      return;
    }
    const { qty } = this.#tier(index);

    while (this.#picked() > qty) {
      const i = this.#history.pop();
      if (i === undefined) break;
      this.#setCount(i, Math.max(0, this.#count(i) - 1));
    }
    if (this.#picked() < qty && rows.length) {
      let target = rows.findIndex((r, i) => r.dataset.default === 'true' && this.#available(i));
      if (target === -1) target = rows.findIndex((_r, i) => this.#available(i));
      if (target !== -1) {
        while (this.#picked() < qty) {
          this.#setCount(target, this.#count(target) + 1);
          this.#history.push(target);
        }
      }
    }
    rows.forEach((_r, i) => this.#setCount(i, this.#count(i)));
    this.#render();
  }

  /** on:click="/increase/{index}" */
  increase(arg) {
    const i = Number(arg);
    if (!this.#available(i)) return;
    this.#preview(i);
    if (!this.#hasTiers) {
      if (this.#picked() >= 10) return;
      this.#setCount(i, this.#count(i) + 1);
      this.#history.push(i);
      this.#render();
      return;
    }
    const { qty } = this.#tier();
    if (this.#picked() >= qty) {
      // Full: move one piece from the oldest other colour to this one.
      const from = this.#history.findIndex((h) => h !== i);
      if (from === -1) return;
      const [donor] = this.#history.splice(from, 1);
      if (donor === undefined) return;
      this.#setCount(donor, this.#count(donor) - 1);
    }
    this.#setCount(i, this.#count(i) + 1);
    this.#history.push(i);
    this.#render();
  }

  /** on:click="/decrease/{index}" */
  decrease(arg) {
    const i = Number(arg);
    if (this.#count(i) <= 0) return;
    // Without tiers keep at least one piece in the order
    if (!this.#hasTiers && this.#picked() <= 1) return;
    this.#setCount(i, this.#count(i) - 1);
    const at = this.#history.lastIndexOf(i);
    if (at !== -1) this.#history.splice(at, 1);
    this.#render();
  }

  /** on:click="/charmsOn" */
  charmsOn() {
    this.#setCharmMode(true);
  }

  /** on:click="/charmsOff" */
  charmsOff() {
    this.#setCharmMode(false);
  }

  /** @param {boolean} on */
  #setCharmMode(on) {
    const { charmYes, charmNo, charmPanel } = this.refs;
    this.#charmsOn = on;
    charmYes?.setAttribute('aria-checked', String(on));
    charmNo?.setAttribute('aria-checked', String(!on));
    if (charmPanel) charmPanel.hidden = !on;
    this.#render();
  }

  /** on:click="/toggleCharm/{index}" */
  toggleCharm(arg) {
    const charm = this.refs.charms?.[Number(arg)];
    if (!charm || charm.disabled) return;
    const on = charm.getAttribute('aria-pressed') !== 'true';
    // Band offers: one Claspie per band (the deal is one Claspie at €4 less)
    if (this.#bandOn && on) (this.refs.charms || []).forEach((c) => c.setAttribute('aria-pressed', 'false'));
    charm.setAttribute('aria-pressed', String(on));
    this.#render();
  }

  /* ---------- Band offers (snippets/cl-band-offer.liquid) ---------- */

  get #bandOn() {
    return (this.refs.bandOpts || []).length > 0;
  }

  #bandTier() {
    const checked = (this.refs.bandOpts || []).find((o) => o.checked);
    return Number(checked?.dataset.tier || 0);
  }

  #second() {
    return (this.refs.seconds || []).find((b) => b.getAttribute('aria-pressed') === 'true') || null;
  }

  /** on:change="/selectBandTier" */
  selectBandTier() {
    const tier = this.#bandTier();
    this.#charmsOn = tier >= 1;
    if (this.refs.charmPanel) this.refs.charmPanel.hidden = tier < 1;
    if (this.refs.bandPick) this.refs.bandPick.hidden = tier < 2;
    this.#updateTier3();
    this.#render();
  }

  /** on:click="/pickSecond/{index}" */
  pickSecond(arg) {
    const pick = this.refs.seconds?.[Number(arg)];
    if (!pick) return;
    // The same band can be in the row and in the sheet: every copy gets the tick
    const id = pick.dataset.variantId;
    (this.refs.seconds || []).forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.variantId === id)));
    this.#updateTier3();
    this.#render();
    if (pick.closest('dialog')) window.setTimeout(() => this.closeBandSheet(), 250);
  }

  /** on:click="/openBandSheet" */
  openBandSheet() {
    const sheet = this.refs.bandSheet;
    if (!sheet) return;
    sheet.showModal();
    document.documentElement.style.overflow = 'hidden';
    sheet.addEventListener('close', () => (document.documentElement.style.overflow = ''), { once: true });
    sheet.addEventListener('click', (e) => {
      if (e.target === sheet) this.closeBandSheet();
    });
  }

  /** on:click="/closeBandSheet" */
  closeBandSheet() {
    this.refs.bandSheet?.close();
  }

  /** Cents off one Claspie (price in cents) bought with a band: the whole price while "buy a band, get a Claspie free" runs */
  #bandOff(price) {
    const d = this.refs.bandOffer?.dataset || {};
    return d.free === 'true' ? price : this.#eurOff(Number(d.off || 0));
  }

  /** Cents for the chosen row: with two bands Shopify keeps the bigger saving (second band % or the Claspie €) */
  #bandTotal(tier = this.#bandTier()) {
    const d = this.refs.bandOffer?.dataset || {};
    const band = Number(this.refs.rows?.[0]?.dataset.price || this.dataset.unitPrice || 0);
    const picked = this.#selectedCharms()[0];
    const charm = Number(picked?.dataset.price || d.charm || 0);
    const off = this.#bandOff(charm);
    if (tier === 0) return band;
    if (tier === 1) return band + charm - off;
    const second = this.#second();
    const price2 = Number(second?.dataset.price || 0);
    const save2 = Number(second?.dataset.save || 0);
    return band + price2 + charm - Math.max(save2, off);
  }

  #updateTier3() {
    const { tier3Price, tier3Was, bandOffer, secondPicked } = this.refs;
    const picked = this.#second();
    if (secondPicked) {
      secondPicked.hidden = !picked;
      if (picked) {
        const after = Number(picked.dataset.price || 0) - Number(picked.dataset.save || 0);
        secondPicked.textContent = `✓ ${picked.dataset.name || ''} · ${this.#money(after, true)}`;
      }
    }
    if (!tier3Price) return;
    const band = Number(this.refs.rows?.[0]?.dataset.price || 0);
    const charm = Number(bandOffer?.dataset.charm || 0);
    const second = this.#second();
    if (!second) return;
    const full = band + Number(second.dataset.price || 0) + charm;
    const off = this.#bandOff(charm);
    tier3Price.textContent = this.#money(full - Math.max(Number(second.dataset.save || 0), off), true);
    if (tier3Was) tier3Was.textContent = this.#money(full, true);
  }

  /** on:click="/showAllCharms" */
  showAllCharms() {
    this.#charmsExpanded = true;
    this.querySelectorAll('.cl-upsell__item[data-extra]').forEach((item) => {
      if (item instanceof HTMLElement) item.hidden = false;
    });
    if (this.refs.charmMore) this.refs.charmMore.hidden = true;
  }

  /**
   * Gold / Silver / Crystal pills: show charms whose tags include the filter. "all" restores
   * the first grid and the "See more" button.
   * on:click="/filterCharms/{tag}"
   * @param {string} arg
   */
  filterCharms(arg) {
    const tag = String(arg || 'all');
    this.querySelectorAll('.cl-upsell__filters [data-filter]').forEach((pill) => {
      pill.setAttribute('aria-pressed', String(pill.getAttribute('data-filter') === tag));
    });
    this.querySelectorAll('.cl-upsell__item').forEach((item) => {
      if (!(item instanceof HTMLElement)) return;
      if (tag === 'all') {
        item.hidden = item.hasAttribute('data-extra') && !this.#charmsExpanded;
      } else {
        item.hidden = !(item.dataset.tags || '').split(' ').includes(tag);
      }
    });
    if (this.refs.charmMore) this.refs.charmMore.hidden = tag !== 'all' || this.#charmsExpanded;
  }

  /* ---------- Claspie packs ---------- */

  get #packOn() {
    return (this.refs.packs || []).length > 0;
  }

  #packSize() {
    const checked = (this.refs.packs || []).find((p) => p.checked);
    return Number(checked?.dataset.size || 1);
  }

  /**
   * Price of n Claspies with the packs (the automatic discounts do the real maths at checkout): this Claspie + the
   * picked ones at their own prices (stones €24 since 26 Oct), empty slots at the standard price, minus the pack's
   * € off per Claspie (snippets/cl-pack.liquid).
   */
  #packPrice(n) {
    const d = this.refs.pack?.dataset || {};
    const sum = this.#packFull(n);
    const off = n === 2 ? Number(d.off2 || 0) : n >= 3 ? Number(d.off3 || 0) : 0;
    // Monthly promotion (snippets/cl-promo.liquid): from promoMin Claspies, promoOff € off each, when that is better
    const promoOff = n >= Number(d.promoMin || 99) ? Number(d.promoOff || 0) : 0;
    return sum - this.#eurOff(Math.max(off, promoOff)) * n;
  }

  /**
   * Euro settings in the visitor's currency (Cris, 3 Nov; window.ClaspLabFx in assets/clasplab-cart-extras.js): a fixed
   * € off per item as Shopify converts it, and the standard Claspie price as Shopify shows it. NaN when it can't.
   * @param {number} cents
   */
  #eurOff(cents) {
    // @ts-ignore set by assets/clasplab-cart-extras.js
    const fx = window.ClaspLabFx;
    return cents ? (fx ? fx.off(cents) : NaN) : 0;
  }

  /** @param {number} cents */
  #eurPrice(cents) {
    // @ts-ignore set by assets/clasplab-cart-extras.js
    const fx = window.ClaspLabFx;
    return fx ? fx.price(cents) : NaN;
  }

  /** n Claspies at their own prices, before any pack saving: this one + the picks + the standard price for empty slots */
  #packFull(n) {
    const d = this.refs.pack?.dataset || {};
    const unit = Number(d.unit || this.dataset.unitPrice || 0);
    const base = d.base ? this.#eurPrice(Number(d.base)) : unit;
    const mates = this.refs.mates || [];
    const picked = this.#picks
      .slice(0, Math.max(0, n - 1))
      .map((id) => Number(mates.find((m) => m.dataset.variantId === id)?.dataset.price || base));
    return unit + picked.reduce((a, b) => a + b, 0) + Math.max(0, n - 1 - picked.length) * base;
  }

  /**
   * Pack rows follow the picks (Cris, 2 Nov: "4 Claspies €39" while a stone Claspie made it €45 in the cart):
   * each row's price, crossed-out price, "€X each" and "save €X" use the picked Claspies' real prices.
   */
  #renderPackRows() {
    for (const input of this.refs.packs || []) {
      const n = Number(input.dataset.size || 1);
      const row = input.closest('.cl-pack__opt');
      const priceEl = row?.querySelector('.cl-pack__price');
      if (!row || !priceEl) continue;
      const price = this.#packPrice(n);
      const full = this.#packFull(n);
      priceEl.replaceChildren(this.#money(price));
      if (full > price) {
        const s = document.createElement('s');
        s.textContent = this.#money(full);
        priceEl.append(s);
      }
      const tag = row.querySelector('.cl-pack__tag');
      if (tag) tag.textContent = tag.textContent.replace(/save .*$/, `save ${this.#money(full - price)}`);
      const sub = row.querySelector('.cl-pack__sub');
      // "CHF 12.33 each" / "146,15 kr each": everything before " each" is the amount
      if (sub && n > 1 && n <= 3) sub.textContent = sub.textContent.replace(/^.+? each/, `${this.#money(Math.round(price / n))} each`);
    }
  }

  /** Biggest pack on offer (3, or the promotion's size while it runs) */
  get #packMax() {
    return (this.refs.packs || []).reduce((max, p) => Math.max(max, Number(p.dataset.size || 0)), 1);
  }

  /** on:change="/selectPack" */
  selectPack() {
    const size = this.#packSize();
    this.#picks = this.#picks.slice(0, Math.max(0, size - 1));
    if (this.refs.packPick) this.refs.packPick.hidden = size < 2;
    this.#renderPack();
    this.#render();
  }

  /** on:click="/toggleMate/{index}": the same Claspie can sit in the row and in the sheet */
  toggleMate(arg) {
    const button = this.refs.mates?.[Number(arg)];
    if (!button || button.disabled) return;
    const id = button.dataset.variantId || '';
    const at = this.#picks.indexOf(id);
    if (this.#bandOn) {
      // Band pages: one Claspie with the band (the deal is one Claspie per band); a new tap swaps it
      this.#picks = at !== -1 ? [] : [id];
      // Picked in the sheet: close it, the button now says "Add to cart" (design B, Cris 3 Nov)
      if (this.#picks.length && button.closest('dialog')) window.setTimeout(() => this.closePackSheet(), 250);
    } else if (at !== -1) {
      this.#picks.splice(at, 1);
    } else {
      let size = this.#packSize();
      // A full 2-pack moves up to 3 (the better deal); a full 3-pack swaps out the oldest pick
      if (this.#picks.length >= size - 1 && size < this.#packMax) {
        const next = (this.refs.packs || []).find((p) => Number(p.dataset.size) === size + 1);
        if (next) {
          next.checked = true;
          size += 1;
          if (this.refs.packPick) this.refs.packPick.hidden = false;
        }
      }
      if (this.#picks.length >= size - 1) this.#picks.shift();
      this.#picks.push(id);
    }
    this.#renderPack();
    this.#render();
  }

  /** on:click="/clearSlot/{index}" */
  clearSlot(arg) {
    const i = Number(arg);
    if (i >= this.#picks.length) {
      this.openPackSheet();
      return;
    }
    this.#picks.splice(i, 1);
    this.#renderPack();
    this.#render();
  }

  /** on:click="/openPackSheet" */
  openPackSheet() {
    const sheet = this.refs.packSheet;
    if (!sheet) return;
    if (this.#packOn && this.#packSize() < 2) {
      // Opening the full list means "more than one": start with the best deal
      const three = (this.refs.packs || []).find((p) => Number(p.dataset.size) === 3);
      if (three) {
        three.checked = true;
        this.selectPack();
      }
    }
    sheet.showModal();
    document.documentElement.style.overflow = 'hidden';
    sheet.addEventListener('close', () => (document.documentElement.style.overflow = ''), { once: true });
    sheet.addEventListener('click', this.#onSheetBackdrop);
  }

  /** on:click="/closePackSheet" */
  closePackSheet() {
    this.refs.packSheet?.close();
  }

  /** @param {MouseEvent} event */
  #onSheetBackdrop = (event) => {
    if (event.target === this.refs.packSheet) this.closePackSheet();
  };

  /** on:click="/packVibe/{tag}" */
  packVibe(arg) {
    this.#packVibe = String(arg || 'all');
    this.#filterSheet();
  }

  /** on:click="/packMetal/{tag}" */
  packMetal(arg) {
    this.#packMetal = String(arg || 'all');
    this.#filterSheet();
  }

  #filterSheet() {
    const sheet = this.refs.packSheet;
    if (!sheet) return;
    sheet.querySelectorAll('[data-vibe]').forEach((b) => b.setAttribute('aria-pressed', String(b.getAttribute('data-vibe') === this.#packVibe)));
    sheet.querySelectorAll('[data-metal]').forEach((b) => b.setAttribute('aria-pressed', String(b.getAttribute('data-metal') === this.#packMetal)));
    let visible = 0;
    sheet.querySelectorAll('.cl-pack-mate__item').forEach((item) => {
      if (!(item instanceof HTMLElement)) return;
      const tags = (item.dataset.tags || '').split(' ');
      const show =
        (this.#packVibe === 'all' || tags.includes(this.#packVibe)) && (this.#packMetal === 'all' || tags.includes(this.#packMetal));
      item.hidden = !show;
      if (show) visible++;
    });
    if (this.refs.packEmpty) this.refs.packEmpty.hidden = visible > 0;
  }

  /** Slots, ticks on every copy of a picked Claspie, the sheet's counter */
  #renderPack() {
    this.#renderPackRows();
    const size = this.#packSize();
    const { slots = [], mates = [], packCount } = this.refs;
    const byId = (/** @type {string} */ id) => mates.find((m) => m.dataset.variantId === id);
    slots.forEach((slot, i) => {
      slot.hidden = i + 1 >= size;
      const id = this.#picks[i];
      const mate = id ? byId(id) : null;
      const img = slot.querySelector('img');
      const name = slot.querySelector('.cl-pack__slot-name');
      slot.classList.toggle('is-filled', Boolean(mate));
      // Empty slots stay tappable: they open the list (clearSlot), a filled one removes its Claspie
      slot.setAttribute('aria-label', mate ? `Remove ${mate.dataset.name || ''}` : 'Pick a Claspie');
      if (img instanceof HTMLImageElement) {
        img.hidden = !mate?.dataset.image;
        if (mate?.dataset.image) img.src = mate.dataset.image;
      }
      if (name) name.textContent = mate ? mate.dataset.name || '' : 'Pick one';
    });
    mates.forEach((m) => m.setAttribute('aria-pressed', String(this.#picks.includes(m.dataset.variantId || ''))));
    if (this.#bandOn && this.refs.charmPicked) {
      const mate = mates.find((m) => m.dataset.variantId === this.#picks[0]);
      const price = Number(mate?.dataset.price || 0);
      const after = price - this.#bandOff(price);
      this.refs.charmPicked.hidden = !mate;
      const line = this.refs.charmPickedText || this.refs.charmPicked;
      if (mate) line.textContent = `✓ ${mate.dataset.name} · ${after <= 0 ? 'free' : this.#money(after)} with this band`;
    }
    if (packCount && this.#bandOn) {
      const mate = mates.find((m) => m.dataset.variantId === this.#picks[0]);
      const price = Number(mate?.dataset.price || 0);
      const after = price - this.#bandOff(price);
      packCount.textContent = mate
        ? `${mate.dataset.name} · ${after <= 0 ? 'free' : this.#money(after)} with this band`
        : 'Pick one Claspie';
    } else if (packCount) {
      const missing = Math.max(0, size - 1 - this.#picks.length);
      const n = 1 + this.#picks.length;
      const next = this.#packPrice(n + 1) - this.#packPrice(n);
      packCount.textContent =
        size < 2
          ? ''
          : missing > 0
            ? `${n} of ${size} picked · pick ${missing} more`
            : size < this.#packMax
              ? next <= 0
                ? `${n} of ${size} picked · ${n === 3 ? 'a 4th' : 'one more'} is free`
                : `${n} of ${size} picked · ${n === 2 ? 'a third' : 'one more'} is just ${this.#money(next)}`
              : `${n} of ${size} picked · ${this.#money(this.#packPrice(size))}`;
    }
  }

  #selectedCharms() {
    if (this.#packOn || (this.#bandOn && (this.refs.mates || []).length)) {
      const mates = this.refs.mates || [];
      return this.#picks.map((id) => mates.find((m) => m.dataset.variantId === id)).filter((m) => m && !m.disabled);
    }
    if (!this.#charmsOn) return [];
    return (this.refs.charms || []).filter((c) => c.getAttribute('aria-pressed') === 'true' && !c.disabled);
  }

  #totalCents() {
    if (this.#packOn) return this.#packPrice(this.#packSize());
    if (this.#bandOn) return this.#bandTotal();
    const bands = (this.refs.rows || []).reduce((sum, row, i) => sum + this.#count(i) * Number(row.dataset.price || 0), 0);
    const charms = this.#selectedCharms().reduce((sum, c) => sum + Number(c.dataset.price || 0), 0);
    return bands + charms;
  }

  #render() {
    const { total, submit, hint, progressFill, steps = [], tiers = [], rows = [] } = this.refs;
    const tier = this.#tier();
    const packMissing = this.#packOn ? Math.max(0, this.#packSize() - 1 - this.#picks.length) : 0;
    const bandTier = this.#bandOn ? this.#bandTier() : 0;
    const bandMissing = this.#bandOn && bandTier >= 1 && !this.#selectedCharms().length;
    const missing = Math.max(0, tier.qty - this.#picked());
    const unitCents = Number(this.dataset.unitPrice || 0);
    const payCents = this.#totalCents() - tier.free * unitCents;

    // "−" is off at zero (and on the last piece without tiers); "+" stays on.
    const lastPiece = !this.#hasTiers && this.#picked() <= 1;
    rows.forEach((row, i) => {
      const [minus, plus] = row.querySelectorAll('.cl-stepper__btn');
      if (minus instanceof HTMLButtonElement) minus.disabled = this.#count(i) === 0 || lastPiece;
      if (plus instanceof HTMLButtonElement) plus.disabled = !this.#available(i);
    });

    if (total) total.textContent = this.#money(Math.max(0, payCents));
    // Still waiting for Claspie picks: the button stays black and active, says what's missing ("Choose your Claspie",
    // "Pick 2 more") and a tap opens the list (21 Oct: a greyed button did nothing; 3 Nov, design B: never greyed)
    this.#waiting = packMissing > 0 || bandMissing;
    if (submit) {
      submit.disabled = submit.hasAttribute('data-sold-out') || missing > 0 || this.#busy;
      submit.toggleAttribute('data-waiting', this.#waiting);
      submit.removeAttribute('aria-disabled');
    }
    const { submitLabel } = this.refs;
    if (submitLabel && this.#packOn) {
      submitLabel.textContent = packMissing > 0 ? `Pick ${packMissing} more` : submitLabel.dataset.label || '';
    }
    if (submitLabel && this.#bandOn) {
      submitLabel.textContent = bandMissing ? 'Choose your Claspie' : submitLabel.dataset.label || '';
    }

    if (hint) {
      const pickMore = this.dataset.textPickMore || 'Pick {count} more.';
      const toShipping = this.dataset.textToShipping || 'Add {amount} more for free shipping.';
      const nextTier = this.dataset.textNextTier || 'Next: {label}.';
      const freeShipping = this.dataset.textFreeShipping || 'Free shipping included.';
      if (missing > 0) {
        hint.textContent = pickMore.replace('{count}', String(missing));
      } else if (this.#threshold && payCents < this.#threshold) {
        // Amount in bold ("Add €26,01 for free EU shipping", Cris 23 Oct)
        const [before, after = ''] = toShipping.split('{amount}');
        const amount = document.createElement('strong');
        amount.textContent = this.#money(this.#threshold - payCents);
        hint.replaceChildren(before, amount, after);
      } else if (this.#threshold) {
        hint.textContent = freeShipping;
      } else {
        const next = this.#tier(this.#tierIndex() + 1);
        hint.textContent = tiers[this.#tierIndex() + 1] ? nextTier.replace('{label}', next.label) : '';
      }
    }

    // Free-shipping bar under the hint
    const { shipFill } = this.refs;
    if (shipFill && !this.#threshold && shipFill.parentElement) shipFill.parentElement.hidden = true;
    if (shipFill && this.#threshold) {
      const track = shipFill.parentElement;
      if (track) track.hidden = missing > 0;
      shipFill.style.width = `${Math.min(100, (Math.max(0, payCents) / this.#threshold) * 100)}%`;
    }

    this.#syncExpress();

    const index = this.#tierIndex();
    if (progressFill && tiers.length > 1) progressFill.style.width = `${(index / (tiers.length - 1)) * 100}%`;
    steps.forEach((s, i) => s.classList.toggle('cl-bundle__step--done', i <= index));
  }

  /** The choice is complete (enough pieces, pack and band offer filled in) */
  #ready() {
    if (this.#picked() < this.#tier().qty) return false;
    if (this.#packOn && this.#picks.length < this.#packSize() - 1) return false;
    if (this.#bandOn && this.#bandTier() >= 1 && !this.#selectedCharms().length) return false;
    return true;
  }

  /** What the current choice puts in the cart: variant id → quantity @returns {Map<number, number>} */
  #grouped() {
    /** @type {Map<number, number>} */
    const grouped = new Map();
    (this.refs.rows || []).forEach((row, i) => {
      const id = Number(row.dataset.variantId || 0);
      if (id && this.#count(i) > 0) grouped.set(id, (grouped.get(id) || 0) + this.#count(i));
    });
    for (const charm of this.#selectedCharms()) {
      const id = Number(charm.dataset.variantId || 0);
      if (id) grouped.set(id, (grouped.get(id) || 0) + 1);
    }
    // Band offers: the second band (in stock only, so never a pre-order line)
    if (this.#bandOn && this.#bandTier() === 2) {
      const id = Number(this.#second()?.dataset.variantId || 0);
      if (id) grouped.set(id, (grouped.get(id) || 0) + 1);
    }
    return grouped;
  }

  /** Express checkout (Apple Pay etc., blocks/bundle-tiers.liquid) only while the choice is one of this item */
  #syncExpress() {
    const { express } = this.refs;
    if (!(express instanceof HTMLElement)) return;
    const grouped = this.#ready() ? this.#grouped() : new Map();
    const id = Number(express.dataset.variantId || 0);
    express.hidden = !(grouped.size === 1 && grouped.get(id) === 1);
  }

  /** on:click="/add" */
  async add() {
    if (this.#busy) return;
    const { submit, error } = this.refs;
    if (this.#waiting && this.refs.packSheet) {
      this.openPackSheet();
      return;
    }
    if (!this.#ready()) return;
    const grouped = this.#grouped();
    // Pre-order lines carry the "Pre-order" property (snippets/cl-preorder.liquid → window.ClaspLabPreorder)
    const preIds = new Set();
    if (this.dataset.preorder === 'true') (this.refs.rows || []).forEach((row) => preIds.add(Number(row.dataset.variantId || 0)));
    for (const charm of this.#selectedCharms()) {
      if (charm.dataset.preorder === 'true') preIds.add(Number(charm.dataset.variantId || 0));
    }
    // @ts-ignore set by snippets/cl-preorder.liquid when pre-orders are on
    const preorder = window.ClaspLabPreorder;
    const items = [...grouped.entries()].map(([id, quantity]) =>
      preorder ? preorder.line({ id, quantity }, preIds.has(id)) : { id, quantity }
    );
    if (!items.length) return;

    this.#busy = true;
    if (submit) submit.setAttribute('aria-busy', 'true');
    if (error) error.hidden = true;

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      }).then((r) => r.json());

      const cart = await this.#fetchCart();
      const didError = Boolean(response.status);
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'cl-bundle-component',
          sourceId: this.id || 'cl-bundle',
          itemCount: items.reduce((s, i) => s + i.quantity, 0),
          productId: this.dataset.productId,
          sections: response.sections,
          didError,
        },
      });
      if (didError) {
        this.dispatchEvent(new CartErrorEvent({ error: response.message || 'Add to cart failed', code: 'INVALID' }));
        if (error) {
          error.textContent = response.description || response.message || 'Could not add to cart.';
          error.hidden = false;
        }
      }
    } catch (e) {
      console.error(e);
      deferred.reject(e);
      if (error) {
        error.textContent = 'Could not add to cart. Please try again.';
        error.hidden = false;
      }
    } finally {
      this.#busy = false;
      if (submit) submit.removeAttribute('aria-busy');
      this.#render();
    }
  }

  async #fetchCart() {
    const cartItems = document.querySelector('cart-items-component');
    if (cartItems && typeof (/** @type {any} */ (cartItems)).fetchCartData === 'function') {
      await customElements.whenDefined('cart-items-component');
      return /** @type {any} */ (cartItems).fetchCartData();
    }
    return fetch(`${Theme.routes.cart_url}.json`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then((r) => r.json());
  }

  /** @param {number} cents @param {boolean} [keepZeros] €24,00 instead of €24 (band prices) */
  #money(cents, keepZeros = false) {
    // A euro deal that can't be converted into this currency (window.ClaspLabFx): no number rather than a wrong one
    if (!Number.isFinite(cents)) return '';
    const format = this.#moneyFormat;
    const value = cents / 100;
    const fixed = value.toFixed(2);
    const [int, dec] = fixed.split('.');
    const group = (s, sep) => s.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
    const map = {
      amount: `${group(int, ',')}.${dec}`,
      amount_no_decimals: group(Math.round(value).toString(), ','),
      amount_with_comma_separator: `${group(int, '.')},${dec}`,
      amount_no_decimals_with_comma_separator: group(Math.round(value).toString(), '.'),
      amount_with_space_separator: `${group(int, ' ')},${dec}`,
      amount_no_decimals_with_space_separator: group(Math.round(value).toString(), ' '),
      amount_with_apostrophe_separator: `${group(int, "'")}.${dec}`,
    };
    const out = format.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) => map[key] ?? map.amount);
    // Whole amounts without trailing zeros (€49, not €49,00), like the money_without_trailing_zeros filter
    return cents % 100 === 0 && !keepZeros ? out.replace(/([.,])00(?!\d)/, '') : out;
  }
}

if (!customElements.get('cl-bundle-component')) customElements.define('cl-bundle-component', ClaspLabBundle);
